import { User } from "firebase/auth";

import { WorkoutDocument } from "@/types";
import { upgradeDocument } from "@/utils/workouts/upgrade-document";
import fixtureData from "@/fixtures/workouts.json";

// Fixture mode: run the app against local sample data with a fake signed-in
// user — no Firebase reads or writes. Enable with `VITE_FIXTURES=1 npm run dev`.
export const FIXTURES_ENABLED = import.meta.env.VITE_FIXTURES === "1";

// Read-only mode: real login, real data, but every Firestore write throws.
// Used to safely validate the v2 UI against real history before any
// migration exists. Enable with `VITE_READONLY=1 npm run dev`.
export const READONLY_ENABLED = import.meta.env.VITE_READONLY === "1";

export function assertWritable(operation: string) {
  if (READONLY_ENABLED) {
    throw new Error(
      `Read-only mode: blocked ${operation}. Writes are disabled (VITE_READONLY=1).`
    );
  }
}

export const FIXTURE_USER = {
  uid: "fixture-user",
  displayName: "Jimmy Longley",
  email: "jimmy@example.com",
  photoURL: null,
} as unknown as User;

type FixtureDoc = {
  id: string;
  daysAgo: number;
  isTemplate: boolean;
  isShared: boolean;
  // "legacy" docs keep the pre-v2 text conventions to exercise the dual-read
  // parser; "v2" docs are upgraded through the real transform at load time.
  format?: "legacy" | "v2";
  data: WorkoutDocument["data"];
};

// Vet empty states: VITE_FIXTURES_EMPTY=1 keeps fixture mode (no real data
// touched) but returns zero workouts.
export const FIXTURES_EMPTY = import.meta.env.VITE_FIXTURES_EMPTY === "1";

export function getFixtureWorkoutDocuments(): Array<WorkoutDocument> {
  if (FIXTURES_EMPTY) return [];
  const now = new Date();
  return (fixtureData as FixtureDoc[])
    .map((doc) => {
      const created = new Date(now);
      created.setDate(created.getDate() - doc.daysAgo);
      // Give docs a plausible morning start time.
      created.setHours(8, 12, 0, 0);
      return {
        id: doc.id,
        created,
        data:
          doc.format === "v2" ? upgradeDocument(doc.data) : doc.data,
        isTemplate: doc.isTemplate,
        isShared: doc.isShared,
        isEditable: true,
      };
    })
    .sort((a, b) => b.created.getTime() - a.created.getTime());
}
